import wretch from 'wretch';

import { type LoadServerCxModuleOptions } from '@cx-modules/client';
import { configureErrorCatchers } from '@cx-modules/errors';
import { getEnvContextualizedSportsCatalogueEndpoint } from '@cx-modules/utils';

import {
  type OutrightsCouponResponseData,
  type Subevent,
  type SubeventsResponseData,
} from '../interfaces/outright-markets.interface';
import { type LoadOutrightsCouponArgs } from '../types';

export type OutrightsCouponRequestParams = Omit<LoadOutrightsCouponArgs, 'dataApiEndpoint'> & LoadServerCxModuleOptions;

export type Breadcrumb = {
  name: string;
  url: string;
};

export type RelatedSubevent = {
  subeventName: string;
  subeventStartTime: string;
  subeventUrl: string;
};

export const transformPayload = (data: SubeventsResponseData): OutrightsCouponResponseData => {
  return {
    outrightMarkets: data.subevents?.flatMap(subevent => {
      return subevent.markets?.map(market => {
        return {
          bets: market.bets.map(bet => {
            const bestOddsBookmakerCodes: string[] = bet.odds.reduce((codes: string[], odd) => {
              codes.push(odd.bookmakerCode);

              return codes;
            }, []);

            return {
              bestOddsAmerican: bet.odds[0].american,
              bestOddsBookmakerCodes,
              bestOddsDecimal: bet.odds[0].decimal,
              bestOddsFractional: bet.odds[0].fractional,
              betId: bet.id,
              betName: bet.name,
              line: bet.line,
            };
          }),
          categoryGroupId: subevent.categoryGroupId,
          eventName: subevent.eventName,
          marketId: market.id,
          marketName: market.name,
          subEventName: subevent.name,
          url: market.urlPath,
        };
      });
    }),
  };
};

const SUBEVENT_LIMIT_DEFAULT = 100;

export const fetchOutrightsCouponData = async (
  dataApiEndpoint = getEnvContextualizedSportsCatalogueEndpoint(),
  params: OutrightsCouponRequestParams,
  options?: LoadServerCxModuleOptions,
): Promise<OutrightsCouponResponseData | null> => {
  const {
    bettypeIds,
    countryCode,
    eventIds,
    overrideBookies,
    replaceMarketBetsMap,
    repub,
    shouldUseSingleIterator,
    subdivision,
    subeventLimit = SUBEVENT_LIMIT_DEFAULT,
  } = params;
  // Option 1 - single flatMap with embedded transform
  if (shouldUseSingleIterator) {
    const [flattenedAndTransformedResponses] = await Promise.all(
      eventIds.map(async eventId => {
        const url = new URL(`${dataApiEndpoint}/v1/subevent-group`);
        url.searchParams.append('eventId', eventId.toString());
        url.searchParams.append('subeventLimit', subeventLimit.toString());
        if (bettypeIds && bettypeIds.length > 0) {
          bettypeIds.forEach(number => {
            url.searchParams.append('bettypeIds', number.toString());
          });
        }
        if (overrideBookies && overrideBookies.length > 0) {
          url.searchParams.append('overrideBookies', overrideBookies.join(','));
        }
        if (replaceMarketBetsMap && replaceMarketBetsMap.length > 0) {
          url.searchParams.append('replaceMarketBetsMap', replaceMarketBetsMap.join(','));
        }

        const client = configureErrorCatchers(
          wretch(url.toString(), {
            headers: {
              accept: 'application/json',
              'content-type': 'application/json',
              'country-code': countryCode,
              repub,
              ...(subdivision ? { subdivision } : {}),
              ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
            },
          }),
          options,
        );

        return client
          .get()
          .notFound(() => null)
          .error(422, () => null)
          .json<SubeventsResponseData>()
          .then(data => {
            if (data === null) {
              return [];
            }
            const transformedData = transformPayload(data);

            return transformedData.outrightMarkets;
          });
      }),
    );

    return {
      outrightMarkets: flattenedAndTransformedResponses,
    };
  }

  // Option 2 - separate map, reduce and transform
  const responses = await Promise.all(
    eventIds.map(async eventId => {
      const url = new URL(`${dataApiEndpoint}/v1/subevent-group`);
      url.searchParams.append('eventId', eventId.toString());
      url.searchParams.append('subeventLimit', subeventLimit.toString());
      if (bettypeIds && bettypeIds.length > 0) {
        bettypeIds.forEach(number => {
          url.searchParams.append('bettypeIds', number.toString());
        });
      }
      if (overrideBookies && overrideBookies.length > 0) {
        url.searchParams.append('overrideBookies', overrideBookies.join(','));
      }
      if (replaceMarketBetsMap && replaceMarketBetsMap.length > 0) {
        url.searchParams.append('replaceMarketBetsMap', replaceMarketBetsMap.join(','));
      }

      const client = configureErrorCatchers(
        wretch(url.toString(), {
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            'country-code': countryCode,
            repub,
            ...(subdivision ? { subdivision } : {}),
            ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
          },
        }),
        options,
      );

      return client
        .get()
        .notFound(() => null)
        .error(422, () => null)
        .json<SubeventsResponseData>();
    }),
  );

  const reducedResponses = responses
    .filter(d => d !== null)
    .reduce((s: Subevent[], d: SubeventsResponseData) => [...s, ...d.subevents], []);

  return transformPayload({
    subevents: reducedResponses,
  });
};

export const getOutrightsCouponCacheKey = ({
  bettypeIds = [],
  countryCode,
  eventIds = [],
  overrideBookies,
  replaceMarketBetsMap,
  repub,
  subdivision,
  subeventLimit = SUBEVENT_LIMIT_DEFAULT,
}: OutrightsCouponRequestParams) => [
  'outrights-coupon',
  repub,
  countryCode,
  subdivision,
  eventIds.join('-'),
  bettypeIds?.join('-') ?? 'none',
  subeventLimit,
  overrideBookies?.join('-') ?? 'none',
  replaceMarketBetsMap?.join('-') ?? 'none',
];
